import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    FaPlay,
    FaStar,
    FaHeart,
    FaBookmark,
    FaSpinner,
    FaChevronRight,
    FaBrain,
    FaLightbulb,
    FaMagic
} from 'react-icons/fa';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

const SimpleRecommendations = () => {
    const [recommendations, setRecommendations] = useState({
        becauseYouLiked: [],
        trending: [],
        similar: [],
        newReleases: []
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { isDark } = useTheme();

    useEffect(() => {
        const fetchRecommendations = async () => {
            try {
                setLoading(true);

                // Fetch user's favorites to base recommendations on - handle missing endpoints gracefully
                let favorites = [];
                try {
                    const favoritesRes = await api.get('/user/favorites');
                    favorites = favoritesRes.data.favorites || [];
                } catch (err) {
                    // Favorites endpoint not available, using empty array
                    favorites = [];
                }

                // Generate different types of recommendations
                const [trendingRes, newReleasesRes, popularRes] = await Promise.all([
                    api.get('/movies/search', { params: { sort_by: 'popularity.desc', from_year: new Date().getFullYear() - 1 } }),
                    api.get('/movies/search', { params: { sort_by: 'release_date.desc', from_year: new Date().getFullYear() } }),
                    api.get('/movies/search', { params: { sort_by: 'vote_average.desc', min_rating: 7 } })
                ]);

                // Simulate "Because you liked" recommendations
                const becauseYouLiked = favorites.length > 0
                    ? trendingRes.data.slice(0, 8).map(movie => ({
                        ...movie,
                        reason: `Because you liked ${favorites[Math.floor(Math.random() * favorites.length)]?.title || 'movies'}`,
                        confidence: Math.floor(Math.random() * 30) + 70 // 70-100% confidence
                    }))
                    : [];

                // Simulate similar movies
                const similar = popularRes.data.slice(0, 6).map(movie => ({
                    ...movie,
                    reason: 'Similar to your favorites',
                    confidence: Math.floor(Math.random() * 20) + 60 // 60-80% confidence
                }));

                setRecommendations({
                    becauseYouLiked,
                    trending: trendingRes.data.slice(0, 10),
                    similar,
                    newReleases: newReleasesRes.data.slice(0, 8)
                });
            } catch (err) {
                console.error('Failed to fetch recommendations:', err);
                setError('Failed to load recommendations');
            } finally {
                setLoading(false);
            }
        };

        fetchRecommendations();
    }, []);

    const MovieCard = ({ movie, showReason = false, showConfidence = false }) => (
        <div className="group relative theme-bg-secondary rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
            <Link to={`/movie/${movie.id}`} className="block">
                <div className="relative overflow-hidden">
                    {movie.poster_path ? (
                        <img
                            src={`https://image.tmdb.org/t/p/w500${movie.poster_path}`}
                            alt={movie.title}
                            className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                    ) : (
                        <div className="w-full h-80 bg-gradient-to-br from-gray-200 to-gray-300 dark:from-gray-700 dark:to-gray-600 flex items-center justify-center">
                            <div className="text-center text-gray-500 dark:text-gray-400">
                                <FaPlay className="text-4xl mx-auto mb-2" />
                                <span className="text-sm">No Image Available</span>
                            </div>
                        </div>
                    )}

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            <FaPlay className="text-white text-3xl bg-white/20 rounded-full p-3 backdrop-blur-sm" />
                        </div>
                    </div>

                    {/* AI Badge */}
                    {showReason && (
                        <div className="absolute top-3 left-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-2 py-1 rounded-full text-xs font-bold flex items-center space-x-1">
                            <FaBrain className="text-xs" />
                            <span>AI</span>
                        </div>
                    )}

                    {/* Rating Badge */}
                    {movie.vote_average > 0 && (
                        <div className="absolute top-3 right-3 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-bold flex items-center space-x-1">
                            <FaStar className="text-xs" />
                            <span>{movie.vote_average.toFixed(1)}</span>
                        </div>
                    )}

                    {/* Confidence Score */}
                    {showConfidence && movie.confidence && (
                        <div className="absolute bottom-3 right-3 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                            {movie.confidence}% match
                        </div>
                    )}
                </div>

                <div className="p-4">
                    <h3 className="font-bold theme-text-primary text-lg mb-2 line-clamp-2 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors">
                        {movie.title}
                    </h3>

                    {showReason && movie.reason && (
                        <p className="text-xs theme-text-tertiary mb-2 italic">
                            {movie.reason}
                        </p>
                    )}

                    {movie.overview && (
                        <p className="theme-text-secondary text-sm line-clamp-2">
                            {movie.overview}
                        </p>
                    )}
                </div>
            </Link>
        </div>
    );

    const RecommendationSection = ({ title, movies, icon: Icon, showReason = false, showConfidence = false }) => {
        if (movies.length === 0) return null;

        return (
            <div className="py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center space-x-3">
                            <div className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg">
                                <Icon className="text-white text-xl" />
                            </div>
                            <div>
                                <h2 className="text-2xl font-bold theme-text-primary">
                                    {title}
                                </h2>
                                <p className="theme-text-secondary text-sm">
                                    Powered by AI recommendations
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
                        {movies.map((movie) => (
                            <MovieCard
                                key={movie.id}
                                movie={movie}
                                showReason={showReason}
                                showConfidence={showConfidence}
                            />
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="py-12">
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <FaSpinner className="text-4xl animate-spin mx-auto mb-4 text-purple-600" />
                        <p className="theme-text-secondary">Loading AI recommendations...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="py-12">
                <div className="text-center">
                    <p className="text-red-600 text-lg">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="py-12">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <div className="flex items-center justify-center space-x-3 mb-4">
                        <FaBrain className="text-4xl text-purple-500" />
                        <h1 className="text-4xl font-bold theme-text-primary">
                            AI Recommendations
                        </h1>
                    </div>
                    <p className="text-xl theme-text-secondary max-w-2xl mx-auto">
                        Discover your next favorite movie with our intelligent recommendation engine
                    </p>
                </div>

                {/* Because You Liked Section */}
                <RecommendationSection
                    title="Because You Liked"
                    movies={recommendations.becauseYouLiked}
                    icon={FaLightbulb}
                    showReason={true}
                    showConfidence={true}
                />

                {/* Similar Movies Section */}
                <RecommendationSection
                    title="Similar Movies"
                    movies={recommendations.similar}
                    icon={FaMagic}
                    showReason={true}
                    showConfidence={true}
                />

                {/* Trending Recommendations */}
                <RecommendationSection
                    title="Trending Now"
                    movies={recommendations.trending}
                    icon={FaStar}
                />

                {/* New Releases */}
                <RecommendationSection
                    title="New Releases"
                    movies={recommendations.newReleases}
                    icon={FaPlay}
                />
            </div>
        </div>
    );
};

export default SimpleRecommendations;
